import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';
import { EditIcon, DeleteIcon, BellIcon, RefreshIcon } from '../components/Icons';

const COLOR_TAGS = [
  { value: 'none', label: 'None', color: 'var(--muted)' },
  { value: 'bullish', label: 'Bullish', color: 'var(--gain)' },
  { value: 'bearish', label: 'Bearish', color: 'var(--loss)' },
  { value: 'neutral', label: 'Neutral', color: '#f59e0b' },
  { value: 'watch', label: 'Watch', color: 'var(--accent)' },
];

const fmt = (n, prefix='$') => n == null ? '—' : prefix + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => n == null ? '—' : (n >= 0 ? '+' : '') + Number(n).toFixed(2) + '%';
const fmtLarge = (n) => {
  if (n == null) return '—';
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
};

export default function Watchlist({ onNavigate }) {
  const [watchlists, setWatchlists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showAddSymbol, setShowAddSymbol] = useState(false);
  const [addForm, setAddForm] = useState({ symbol: '', notes: '', color_tag: 'none' });
  const [editingName, setEditingName] = useState(null);
  const [editNameVal, setEditNameVal] = useState('');
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editSymbolForm, setEditSymbolForm] = useState({ notes: '', color_tag: 'none' });
  const [sortField, setSortField] = useState('sort_order');
  const [sortDir, setSortDir] = useState('asc');
  const [error, setError] = useState('');
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const colDragItem = useRef(null);
  const colDragOver = useRef(null);

  const DEFAULT_COLUMNS = [
    { field: 'current_price', label: 'Price' },
    { field: 'change_pct', label: 'Day %' },
    { field: 'change', label: 'Day $' },
    { field: 'high', label: 'Day High' },
    { field: 'low', label: 'Day Low' },
    { field: 'week_52_high', label: '52W High' },
    { field: 'week_52_low', label: '52W Low' },
    { field: 'ytd_change_pct', label: 'YTD %' },
    { field: 'market_cap', label: 'Mkt Cap' },
    { field: 'pe_ratio', label: 'P/E' },
    { field: 'avg_volume', label: 'Avg Vol' },
    { field: 'float_shares', label: 'Float' },
    { field: 'beta', label: 'Beta' },
    { field: 'delta', label: 'Delta' },
    { field: 'gamma', label: 'Gamma' },
    { field: 'notes', label: 'Notes' },
  ];

  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  const loadLists = () => api.getWatchlists().then(lists => {
    setWatchlists(lists);
    if (lists.length > 0 && !activeId) setActiveId(lists[0].id);
  });

  const loadQuotes = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.getWatchlistQuotes(id);
      setQuotes(data);
    } catch { setQuotes([]); }
    setLoading(false);
  };

  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (activeId) loadQuotes(activeId); }, [activeId]);

  const createList = async () => {
    if (!newListName.trim()) return;
    const w = await api.createWatchlist({ name: newListName.trim() });
    setNewListName('');
    setShowNewList(false);
    await loadLists();
    setActiveId(w.id);
  };

  const deleteList = async (id) => {
    if (!window.confirm('Delete this watchlist?')) return;
    await api.deleteWatchlist(id);
    const remaining = watchlists.filter(w => w.id !== id);
    setWatchlists(remaining);
    setActiveId(remaining.length > 0 ? remaining[0].id : null);
    setQuotes([]);
  };

  const renameList = async (id) => {
    if (!editNameVal.trim()) return;
    await api.renameWatchlist(id, { name: editNameVal.trim() });
    setEditingName(null);
    loadLists();
  };

  const addSymbol = async () => {
    setError('');
    try {
      await api.addSymbolToWatchlist(activeId, {
        symbol: addForm.symbol.toUpperCase(),
        notes: addForm.notes || null,
        color_tag: addForm.color_tag,
      });
      setAddForm({ symbol: '', notes: '', color_tag: 'none' });
      setShowAddSymbol(false);
      loadQuotes(activeId);
    } catch (e) {
      setError('Could not add symbol. It may already be in this list.');
    }
  };

  const removeSymbol = async (symbol) => {
    if (!window.confirm(`Remove ${symbol}?`)) return;
    await api.removeSymbolFromWatchlist(activeId, symbol);
    loadQuotes(activeId);
  };

  const saveSymbolEdit = async () => {
    await api.updateWatchlistSymbol(activeId, editingSymbol, editSymbolForm);
    setEditingSymbol(null);
    loadQuotes(activeId);
  };

  // Drag and drop reorder
  const handleDragStart = (e, index) => { dragItem.current = index; };
  const handleDragEnter = (e, index) => { dragOverItem.current = index; };
  const handleDragEnd = async () => {
    const reordered = [...quotes];
    const dragged = reordered.splice(dragItem.current, 1)[0];
    reordered.splice(dragOverItem.current, 0, dragged);
    setQuotes(reordered);
    dragItem.current = null;
    dragOverItem.current = null;
    await api.reorderWatchlistSymbols(activeId, reordered.map(q => q.id));
  };

  const handleColDragStart = (e, index) => {
    colDragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleColDragEnter = (e, index) => { colDragOver.current = index; };
  const handleColDragEnd = () => {
    if (colDragItem.current === null || colDragOver.current === null) return;
    const reordered = [...columns];
    const dragged = reordered.splice(colDragItem.current, 1)[0];
    reordered.splice(colDragOver.current, 0, dragged);
    setColumns(reordered);
    colDragItem.current = null;
    colDragOver.current = null;
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label }) => (
    <th onClick={() => toggleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const sorted = sortField === 'sort_order' ? quotes : [...quotes].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const activeList = watchlists.find(w => w.id === activeId);
  const colorDot = (tag) => {
    const c = COLOR_TAGS.find(t => t.value === tag);
    return c ? <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.color, marginRight: 6 }} /> : null;
  };

return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 80px)' }}>

      {/* Left sidebar — watchlist selector */}
      <div style={{ width: 200, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Watchlists</span>
          <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => setShowNewList(true)}>+</button>
        </div>

        {showNewList && (
          <div style={{ marginBottom: 12 }}>
            <input value={newListName} onChange={e => setNewListName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createList()}
              placeholder="List name" autoFocus style={{ marginBottom: 6 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn" style={{ flex: 1, padding: '6px' }} onClick={createList}>Create</button>
              <button className="btn-ghost" style={{ flex: 1, padding: '6px' }} onClick={() => setShowNewList(false)}>Cancel</button>
            </div>
          </div>
        )}

        {watchlists.map(w => (
          <div key={w.id} style={{ marginBottom: 4 }}>
            {editingName === w.id ? (
              <div>
                <input value={editNameVal} onChange={e => setEditNameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') renameList(w.id); if (e.key === 'Escape') setEditingName(null); }}
                  autoFocus style={{ marginBottom: 4 }} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn" style={{ flex: 1, padding: '4px', fontSize: 12 }} onClick={() => renameList(w.id)}>Save</button>
                  <button className="btn-ghost" style={{ flex: 1, padding: '4px', fontSize: 12 }} onClick={() => setEditingName(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  onClick={() => setActiveId(w.id)}
                  style={{ flex: 1, textAlign: 'left', background: activeId === w.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                    border: 'none', color: activeId === w.id ? 'var(--accent)' : 'var(--text)',
                    borderRadius: 6, padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}>
                  {w.name}
                </button>
                <button className="icon-btn-sm" title="Rename" aria-label="Rename"
                  onClick={() => { setEditingName(w.id); setEditNameVal(w.name); }}><EditIcon /></button>
                <button className="icon-btn-sm" title="Delete" aria-label="Delete"
                  onClick={() => deleteList(w.id)}><DeleteIcon /></button>
              </div>
            )}
          </div>
        ))}

        {watchlists.length === 0 && (
          <div className="muted" style={{ fontSize: 12, padding: 8 }}>No watchlists yet.</div>
        )}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!activeId ? (
          <div className="card muted" style={{ textAlign: 'center', padding: 60 }}>
            Create a watchlist to get started.
          </div>
        ) : (
          <>
            <div className="page-header">
              <h1 className="page-title">{activeList?.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="icon-btn" title="Refresh" aria-label="Refresh" onClick={() => loadQuotes(activeId)}><RefreshIcon /></button>
                <button className="btn" onClick={() => setShowAddSymbol(true)}>+ Add Symbol</button>
              </div>
            </div>

            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              Drag rows to reorder · Click symbol to view chart · 🟢 = in portfolio · <BellIcon width={11} height={11} style={{ verticalAlign: -1 }} /> = alert active
            </div>
            
            <div style={{ position: 'relative', overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              {loading ? (
                <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Loading quotes...</div>
              ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 24, background: 'var(--surface)', position: 'sticky', left: 0, zIndex: 3, borderBottom: '1px solid var(--border)' }}></th>
                      <th style={{ background: 'var(--surface)', position: 'sticky', left: 24, zIndex: 3, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', padding: '8px 12px', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Symbol</th>
                      {columns.map((col, ci) => (
                        <th key={col.field}
                          draggable
                          onDragStart={e => handleColDragStart(e, ci)}
                          onDragEnter={e => handleColDragEnter(e, ci)}
                          onDragEnd={handleColDragEnd}
                          onDragOver={e => e.preventDefault()}
                          onClick={() => toggleSort(col.field)}
                          style={{ cursor: 'grab', userSelect: 'none', background: 'var(--surface)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', padding: '8px 12px', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {col.label} {sortField === col.field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </th>
                      ))}
                      <th style={{ background: 'var(--surface)', position: 'sticky', right: 0, zIndex: 3, borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', padding: '8px 12px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((q, i) => {
                      const renderCell = (col) => {
                        switch(col.field) {
                          case 'current_price': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.current_price)}</td>;
                          case 'change_pct': return <td key={col.field} className={`mono amount ${q.change_pct >= 0 ? 'gain' : 'loss'}`} style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmtPct(q.change_pct)}</td>;
                          case 'change': return <td key={col.field} className={`mono amount ${q.change >= 0 ? 'gain' : 'loss'}`} style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.change)}</td>;
                          case 'high': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.high)}</td>;
                          case 'low': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.low)}</td>;
                          case 'week_52_high': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.week_52_high)}</td>;
                          case 'week_52_low': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmt(q.week_52_low)}</td>;
                          case 'ytd_change_pct': return <td key={col.field} className={`mono amount ${q.ytd_change_pct >= 0 ? 'gain' : 'loss'}`} style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmtPct(q.ytd_change_pct)}</td>;
                          case 'market_cap': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{fmtLarge(q.market_cap)}</td>;
                          case 'pe_ratio': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.pe_ratio != null ? Number(q.pe_ratio).toFixed(1) : '—'}</td>;
                          case 'avg_volume': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.avg_volume != null ? (q.avg_volume / 1e6).toFixed(1) + 'M' : '—'}</td>;
                          case 'float_shares': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.float_shares != null ? fmtLarge(q.float_shares * 1e6) : '—'}</td>;
                          case 'beta': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.beta != null ? Number(q.beta).toFixed(2) : '—'}</td>;
                          case 'delta': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.delta != null ? Number(q.delta).toFixed(3) : '—'}</td>;
                          case 'gamma': return <td key={col.field} className="mono amount" style={{ padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{q.gamma != null ? Number(q.gamma).toFixed(4) : '—'}</td>;
                          case 'notes': return <td key={col.field} style={{ padding: '12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{q.notes || ''}</td>;
                          default: return <td key={col.field} style={{ padding: '12px', borderBottom: '1px solid var(--border)' }}>—</td>;
                        }
                      };
                      return (
                        <tr key={q.symbol}
                          draggable={sortField === 'sort_order'}
                          onDragStart={e => { if (sortField === 'sort_order') handleDragStart(e, i); }}
                          onDragEnter={e => { if (sortField === 'sort_order') handleDragEnter(e, i); }}
                          onDragEnd={() => { if (sortField === 'sort_order') handleDragEnd(); }}
                          onDragOver={e => e.preventDefault()}
                          style={{ background: 'var(--surface)' }}
                        >
                          <td style={{ width: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 14, padding: '12px 4px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 2 }}>
                            {sortField === 'sort_order' ? '⠿' : ''}
                          </td>
                          <td style={{ position: 'sticky', left: 24, background: 'var(--surface)', zIndex: 2, padding: '12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {colorDot(q.color_tag)}
                              <strong className="mono" style={{ cursor: 'pointer', color: 'var(--accent)' }}
                                title={q.name || q.symbol}
                                onClick={() => onNavigate && onNavigate('charts', { symbol: q.symbol })}>
                                {q.symbol}
                              </strong>
                              {q.in_portfolio && <span title="In portfolio" style={{ fontSize: 10 }}>🟢</span>}
                              {q.has_alert && <span title="Alert active" style={{ display: 'inline-flex', color: '#f59e0b' }}><BellIcon width={11} height={11} /></span>}
                            </div>
                          </td>
                          {columns.map(col => renderCell(col))}
                          <td style={{ position: 'sticky', right: 0, background: 'var(--surface)', zIndex: 2, padding: '8px 12px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                            <button className="icon-btn-sm" style={{ marginRight: 4 }} title="Alert" aria-label="Alert"
                              onClick={() => onNavigate && onNavigate('alerts', { symbol: q.symbol })}><BellIcon /></button>
                            <button className="icon-btn-sm" style={{ marginRight: 4 }} title="Edit" aria-label="Edit"
                              onClick={() => { setEditingSymbol(q.symbol); setEditSymbolForm({ notes: q.notes || '', color_tag: q.color_tag || 'none' }); }}><EditIcon /></button>
                            <button className="icon-btn-sm" title="Remove" aria-label="Remove"
                              onClick={() => removeSymbol(q.symbol)}><DeleteIcon /></button>
                          </td>
                        </tr>
                      );
                    })}
                    {sorted.length === 0 && (
                      <tr><td colSpan={columns.length + 3} className="muted" style={{ textAlign: 'center', padding: 32 }}>No symbols yet — add one above.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add Symbol Modal */}
      {showAddSymbol && (
        <div className="modal-overlay" onClick={() => setShowAddSymbol(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add Symbol</div>
            <div className="form-group">
              <label>Symbol</label>
              <input value={addForm.symbol} onChange={e => setAddForm({ ...addForm, symbol: e.target.value.toUpperCase() })}
                placeholder="AAPL" autoFocus />
            </div>
            <div className="form-group">
              <label>Color Tag</label>
              <select value={addForm.color_tag} onChange={e => setAddForm({ ...addForm, color_tag: e.target.value })}>
                {COLOR_TAGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Notes (optional)</label>
              <input value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="Why you're watching this" />
            </div>
            {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => { setShowAddSymbol(false); setError(''); }}>Cancel</button>
              <button className="btn" onClick={addSymbol}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Symbol Modal */}
      {editingSymbol && (
        <div className="modal-overlay" onClick={() => setEditingSymbol(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Edit {editingSymbol}</div>
            <div className="form-group">
              <label>Color Tag</label>
              <select value={editSymbolForm.color_tag} onChange={e => setEditSymbolForm({ ...editSymbolForm, color_tag: e.target.value })}>
                {COLOR_TAGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <input value={editSymbolForm.notes} onChange={e => setEditSymbolForm({ ...editSymbolForm, notes: e.target.value })}
                placeholder="Why you're watching this" />
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setEditingSymbol(null)}>Cancel</button>
              <button className="btn" onClick={saveSymbolEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}